import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { isNumber } from 'lodash/fp';
// @ts-ignore
import AnimatedNumber from 'react-animated-number';
import { fmtPercent, fmtPrice } from '@upstart/harmony/lib/formatter';

const LOADING_DELAY = 1000; // ms

interface Props {
  loading?: boolean;
  value?: number;
  type: 'price' | 'percentage';
}
/**
 * A price-formatted value display that reduces jitter from payments quickly changing
 * and provides an animation over the numbers changing
 */
const ValueDisplay: FunctionComponent<Props> = props => {
  const { loading } = props;
  const [cachedValue, setCachedValue] = useState(props.value);

  const timer = useRef<NodeJS.Timeout | undefined>();

  useEffect(() => {
    if (isNumber(props.value)) {
      // if the new value is a number, immediately set it and clear the timer
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = undefined;
      }
      setCachedValue(props.value);
    } else if (!timer.current) {
      // if the value is not a number, set a timer to change the cached value
      timer.current = setTimeout(() => {
        setCachedValue(props.value);
        timer.current = undefined;
      }, LOADING_DELAY);
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [props.value]);

  let formatter;
  switch (props.type) {
    case 'price': {
      formatter = fmtPrice;
      break;
    }
    case 'percentage': {
      formatter = fmtPercent;
      break;
    }
  }

  // display the cached value if it's a number
  if (isNumber(cachedValue)) {
    return (
      <AnimatedNumber
        value={cachedValue}
        // setting initial value keeps the animation from showing on page load
        // or after a status indicator was shown
        initialValue={cachedValue}
        duration={400}
        formatValue={formatter}
      />
    );
  }

  return <span>{loading ? 'Loading...' : 'N/A'}</span>;
};

export default ValueDisplay;

export function PaymentDisplay(props: Omit<Props, 'type'>) {
  return <ValueDisplay type="price" {...props} />;
}
export function PercentDisplay(props: Omit<Props, 'type'>) {
  return <ValueDisplay type="percentage" {...props} />;
}
